import { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useWorkforceDetail } from "@/hooks/workforce/useWorkforceDetail";
import { useWorkforceComments } from "@/hooks/workforce/useWorkforceComments";
import { useWorkforceActions } from "@/hooks/workforce/useWorkforceActions";
import { useTrackPageVisit } from "@/hooks/useEntityHistory";
import { WorkforceDetailHeader } from "@/components/workforce/WorkforceDetailHeader";
import { WorkforceProfileSection } from "@/components/workforce/WorkforceProfileSection";
import { WorkforceTabs } from "@/components/workforce/WorkforceTabs";
import { WorkforceStatusChangeModal, BenchCreationData } from "@/components/workforce/WorkforceStatusChangeModal";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Workforce, WorkforceStatus } from "@/types/workforce";
import { supabase } from "@/integrations/supabase/client";

const WorkforceDetail = () => {
	const { id } = useParams<{ id: string }>();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { workforce, loading, refetch } = useWorkforceDetail(id);
	const { comments, handleAddComment, handleDeleteComment, handleUpdateComment } = useWorkforceComments(id);
	const { handleDelete, handleUpdate, handleUpdateStatus } = useWorkforceActions(id, refetch);

	const [activeTab, setActiveTab] = useState("overview");
	const [isEditing, setIsEditing] = useState(false);
	const [editWorkforce, setEditWorkforce] = useState<Workforce | null>(null);
	const [statusChangeTarget, setStatusChangeTarget] = useState<WorkforceStatus | null>(null);

	const createBenchFromWorkforce = useCallback(async (benchData: BenchCreationData) => {
		if (!workforce) return;

		const candidateId = workforce.candidate_id;
		const workforceId = workforce.id;
		const orgUuid = workforce.organization_uuid;

		if (!candidateId || !workforceId || !orgUuid) return;

		try {
			// Check if a bench entry already exists from this workforce
			const { data: existing } = await supabase
				.from('workforce')
				.select('id')
				.eq('created_from', workforceId)
				.eq('is_bench', true)
				.limit(1);

			if (existing && existing.length > 0) {
				toast({
					title: t("workforce.bench.errors.alreadyOnBench", "Already on bench"),
					description: t("workforce.bench.errors.benchAlreadyCreated", "A bench assignment was already created from this assignment"),
					variant: "destructive",
				});
				return;
			}

			const { data: inserted, error } = await supabase.from('workforce').insert({
				organization_uuid: orgUuid,
				candidate_id: candidateId,
				start_date: new Date().toISOString().split('T')[0],
				status: 'active',
				is_bench: true,
				bench_daily_cost: benchData.bench_daily_cost,
				currency: benchData.currency,
				bench_reason: benchData.bench_reason || null,
				work_percentage: benchData.work_percentage || null,
				notes: benchData.notes || null,
				previous_workforce_id: workforceId,
				created_from: workforceId,
			}).select('id').single();

			if (error) throw error;

			toast({
				title: t("workforce.bench.addSuccess", "Added to bench"),
				description: t("workforce.bench.addSuccessDesc", "Consultant has been added to the bench"),
			});
		} catch (error: any) {
			toast({
				title: t("common.error", "Error"),
				description: error?.message || t("workforce.bench.errors.addFailed", "Failed to add to bench"),
				variant: "destructive",
			});
		}
	}, [workforce, t]);

	const handleStatusChangeConfirm = useCallback(async (data: {
		status: WorkforceStatus;
		end_date?: string;
		status_change_reason?: string;
		status_change_note?: string;
		createBench?: BenchCreationData;
	}) => {
		setStatusChangeTarget(null);

		// Update the status
		await handleUpdateStatus(data.status, {
			end_date: data.end_date,
			status_change_reason: data.status_change_reason,
			status_change_note: data.status_change_note,
		});

		// Create bench assignment if requested
		if (data.createBench)
			await createBenchFromWorkforce(data.createBench);
	}, [handleUpdateStatus, createBenchFromWorkforce]);

	usePageTitle(workforce?.candidate?.name ? `${workforce.candidate.name} - Assignment` : "Assignment");
	useTrackPageVisit('workforce', id, workforce?.candidate?.name, {
		companyName: workforce?.job?.company?.name || workforce?.external_company_name || undefined,
	});

	const handleEdit = () => {
		setEditWorkforce(workforce ? { ...workforce } : null);
		setIsEditing(true);
	};

	const handleCancel = () => {
		setIsEditing(false);
		setEditWorkforce(null);
	};

	const handleFieldChange = (field: string, value: any) => {
		if (editWorkforce)
			setEditWorkforce({ ...editWorkforce, [field]: value });
	};

	const handleSave = async () => {
		if (!editWorkforce) return;

		// Check if there are actual changes
		const hasChanges = JSON.stringify(editWorkforce) !== JSON.stringify(workforce);
		if (!hasChanges) {
			setIsEditing(false);
			toast({ title: t("common.noChanges", "No changes"), description: t("common.noChangesToSave", "No changes to save.") });
			return;
		}

		// Close edit mode immediately for better UX
		setIsEditing(false);
		toast({ title: t("common.success", "Success"), description: t("workforce.updateSuccess", "Assignment updated successfully") });

		try {
			const success = await handleUpdate(editWorkforce);
			if (success) {
				// Refetch in background
				refetch();
			} else {
				// Revert to edit mode on failure
				setIsEditing(true);
				toast({ title: t("common.error", "Error"), description: t("workforce.updateError", "Failed to update assignment"), variant: "destructive" });
			}
		} catch (error) {
			setIsEditing(true);
			toast({ title: t("common.error", "Error"), description: t("workforce.updateError", "Failed to update assignment"), variant: "destructive" });
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		);
	}

	if (!workforce) {
		return (
			<div className="flex flex-col items-center justify-center min-h-[400px] text-center">
				<h2 className="text-xl font-semibold mb-2">
					{t("workforce.notFound", "Assignment not found")}
				</h2>
				<p className="text-muted-foreground mb-4">
					{t("workforce.notFoundDesc", "This workforce assignment could not be found.")}
				</p>
				<button
					onClick={() => navigate("/workforce")}
					className="text-primary hover:underline"
				>
					{t("workforce.backToList", "Back to Workforce")}
				</button>
			</div>
		);
	}

	return (
		<div className="w-full">
			{/* Header */}
			<WorkforceDetailHeader
				workforce={workforce}
				isEditing={isEditing}
				onEdit={handleEdit}
				onDelete={handleDelete}
				onStatusChange={(status: string) => setStatusChangeTarget(status as WorkforceStatus)}
				onRefresh={refetch}
			/>

			{/* Profile Section - Full Width */}
			<WorkforceProfileSection
				workforce={workforce}
				isEditing={isEditing}
				editWorkforce={editWorkforce || undefined}
				onChange={handleFieldChange}
				onSave={handleSave}
				onCancel={handleCancel}
				onStatusChange={(status: string) => setStatusChangeTarget(status as WorkforceStatus)}
			/>

			{/* Tabs - Full Width */}
			<WorkforceTabs
				workforce={workforce}
				comments={comments}
				activeTab={activeTab}
				setActiveTab={setActiveTab}
				onAddComment={handleAddComment}
				onDeleteComment={handleDeleteComment}
				onUpdateComment={handleUpdateComment}
			/>

			{/* Status Change Modal */}
			{statusChangeTarget && (
				<WorkforceStatusChangeModal
					isOpen={!!statusChangeTarget}
					onClose={() => setStatusChangeTarget(null)}
					currentStatus={workforce.status}
					targetStatus={statusChangeTarget}
					candidateName={workforce.candidate?.name}
					candidateId={workforce.candidate_id}
					workforceId={workforce.id}
					currentAllocation={workforce.work_percentage}
					currentCurrency={workforce.currency}
					isBench={workforce.is_bench}
					onConfirm={handleStatusChangeConfirm}
				/>
			)}
		</div>
	);
};

export default WorkforceDetail;
